import React, { useMemo, useState, useContext } from 'react'
import { Tooltip, Popover, Button, message } from 'antd';
import { CheckCircleFilled, ClockCircleFilled, MinusCircleFilled } from '@ant-design/icons';
import { getLanguageFlag, getLanguageName, Flag, getAllLanguages } from '../utils';
import { TaskCell } from './TaskCell';
import { Context } from '../context';

// Helper function to generate audio editor URL
// fromEng: true = с английского (from_eng=1), false = с русского (без from_eng)
const getAudioEditorUrl = (projectId, folderId, langId, fromEng = false) => {
  // Map language codes to URL format
  const langMap = {
    'eng': 'en',
    'es': 'es',
    'kz': 'kz',
    'de': 'de',
    'ru': 'ru',
    'rus': 'ru'
  };
  const lang = langMap[langId] || langId;
  const baseUrl = `https://tts-audio.dev.torsunov.pro/fast/${projectId}?folder=${folderId}&lang=${lang}`;
  return fromEng ? `${baseUrl}&from_eng=1&video=1` : `${baseUrl}&video=1`;
};

// Button that opens link on click and shows copy option on hover
const AudioLinkButton = ({ url, label, backgroundColor }) => {
  const handleClick = (e) => {
    e.stopPropagation();
    window.open(url, '_blank');
  };

  const handleCopy = (e) => {
    e.stopPropagation();
    e.preventDefault();
    navigator.clipboard.writeText(url).then(() => {
      message.success('Ссылка скопирована');
    }).catch(() => {
      message.error('Не удалось скопировать ссылку');
    });
  };

  const buttonStyle = {
    color: '#fff',
    fontSize: '8px',
    padding: '2px 6px',
    borderRadius: '3px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 500,
    lineHeight: 'normal',
    backgroundColor,
  };

  return (
    <Popover
      content={
        <div
          style={{ cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap' }}
          onClick={handleCopy}
        >
          Скопировать ссылку
        </div>
      }
      trigger="hover"
      placement="top"
      mouseEnterDelay={0.3}
    >
      <button style={buttonStyle} onClick={handleClick}>
        {label}
      </button>
    </Popover>
  );
};

// Component for audio editor links (from Russian and from English)
// Hide "с EN" button for Russian and English languages
const AudioEditorLinks = ({ projectId, folderId, langId }) => {
  const isRuOrEn = ['ru', 'rus', 'eng', 'en'].includes(langId);

  return (
    <div style={{
      display: 'flex',
      gap: '3px',
      justifyContent: 'center',
      padding: '2px 0'
    }}>
      <AudioLinkButton
        url={getAudioEditorUrl(projectId, folderId, langId, false)}
        label="с RU"
        backgroundColor="#1890ff"
      />
      {!isRuOrEn && (
        <AudioLinkButton
          url={getAudioEditorUrl(projectId, folderId, langId, true)}
          label="с EN"
          backgroundColor="#52c41a"
        />
      )}
    </div>
  );
};

// Компонент мини-прогресс индикатора
const ProgressIndicator = ({ done, inProgress, pending, label }) => {
  const total = done + inProgress + pending;
  if (total === 0) return null;

  const percent = Math.round((done / total) * 100);

  return (
    <Tooltip title={`${label}: ${done}/${total} готово`}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '10px',
        color: done === total ? '#52c41a' : inProgress > 0 ? '#faad14' : '#d9d9d9'
      }}>
        <div style={{
          width: '32px',
          height: '4px',
          backgroundColor: '#f0f0f0',
          borderRadius: '2px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${percent}%`,
            height: '100%',
            backgroundColor: done === total ? '#52c41a' : inProgress > 0 ? '#faad14' : '#d9d9d9',
            transition: 'width 0.3s'
          }} />
        </div>
        <span style={{ minWidth: '24px' }}>{done}/{total}</span>
      </div>
    </Tooltip>
  );
};

// Компактная ячейка статуса для раздела "Полное видео"
const COMPACT_STATUSES = {
  'done': { title: 'Готово', className: 't_status--done' },
  'in_progress': { title: 'В процессе', className: 't_status--progress' },
  'pending': { title: 'Не готово', className: '' }
};

const CompactStatusCell = ({ task, taskType, label, projectId, partId, languageId }) => {
  const { updateTaskStatus, bulkUpdateStatus } = useContext(Context);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const currentStatus = task?.status || 'pending';
  const [selectedStatus, setSelectedStatus] = useState(currentStatus);

  const handleStatusChange = async (newStatus) => {
    try {
      // Для full_video и full_video_proofreading обновляем все части сразу,
      // т.к. видео не разделено на куски — это полное видео
      await bulkUpdateStatus(projectId, taskType, newStatus, languageId);
      setIsPopoverOpen(false);
      message.success('Статус обновлен на всех частях');
    } catch (error) {
      console.error('Error updating status:', error);
      message.error('Ошибка при обновлении статуса');
    }
  };

  const popoverContent = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '-12px' }}>
      <h4>Изменить статус</h4>
      <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>
        Применяется ко всем частям
      </div>
      {Object.entries(COMPACT_STATUSES).map(([status, config]) => (
        <div
          key={status}
          onClick={() => setSelectedStatus(status)}
          style={{
            padding: '6px 8px',
            cursor: 'pointer',
            backgroundColor: selectedStatus === status ? '#e6f7ff' : 'transparent',
            border: selectedStatus === status ? '1px solid #1890ff' : '1px solid transparent',
            borderRadius: '4px',
          }}
        >
          <div className={`t_status ${config.className}`}>{config.title}</div>
        </div>
      ))}
      <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}>
        <Button size="small" onClick={() => setIsPopoverOpen(false)}>Отмена</Button>
        <Button
          size="small"
          type="primary"
          onClick={() => handleStatusChange(selectedStatus)}
          disabled={selectedStatus === currentStatus}
        >
          Применить
        </Button>
      </div>
    </div>
  );

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '3px 0',
    }}>
      <span style={{ fontSize: '11px', color: '#595959', width: '4.5em', textAlign: 'right', flexShrink: 0 }}>{label}</span>
      <Popover
        content={popoverContent}
        trigger="click"
        open={isPopoverOpen}
        onOpenChange={(open) => {
          setIsPopoverOpen(open);
          if (open) setSelectedStatus(currentStatus);
        }}
        placement="bottomLeft"
        destroyTooltipOnHide
      >
        <div
          className={`t_status _click ${COMPACT_STATUSES[currentStatus]?.className || ''}`}
          style={{ cursor: 'pointer' }}
        >
          {COMPACT_STATUSES[currentStatus]?.title || 'Не готово'}
        </div>
      </Popover>
    </div>
  );
};

const PartComponent = (props) => {

  const {
    id,
    project_id,
    length,
    source_file,
    languages = [],
    langsShown = 'all',
    partDuration,  // длительность части из files_to_send
    isSkipped = false  // пропущенная часть (не учитывается в прогрессе)
  } = { ...props };

  // Подсчёт прогресса по категориям
  const progress = useMemo(() => {
    const stats = {
      translate: { done: 0, inProgress: 0, pending: 0 },
      voicing: { done: 0, inProgress: 0, pending: 0 },
      fullVideo: { done: 0, inProgress: 0, pending: 0 },
      playlist: { done: 0, inProgress: 0, pending: 0 },
      s3: { done: 0, inProgress: 0, pending: 0 }
    };

    languages.forEach(lang => {
      // Перевод (auto_translation + translation_proofreading)
      ['auto_translation', 'translation_proofreading'].forEach(taskType => {
        const task = lang[taskType];
        if (task?.status === 'done') stats.translate.done++;
        else if (task?.status === 'in_progress') stats.translate.inProgress++;
        else stats.translate.pending++;
      });

      // Озвучка (voicing + voicing_proofreading)
      ['voicing', 'voicing_proofreading'].forEach(taskType => {
        const task = lang[taskType];
        if (task?.status === 'done') stats.voicing.done++;
        else if (task?.status === 'in_progress') stats.voicing.inProgress++;
        else stats.voicing.pending++;
      });

      // Полное видео (full_video + full_video_proofreading)
      ['full_video', 'full_video_proofreading'].forEach(taskType => {
        const task = lang[taskType];
        if (task?.status === 'done') stats.fullVideo.done++;
        else if (task?.status === 'in_progress') stats.fullVideo.inProgress++;
        else stats.fullVideo.pending++;
      });

      // Плейлисты (ts_playlist)
      const tsTask = lang['ts_playlist'];
      if (tsTask?.status === 'done') stats.playlist.done++;
      else if (tsTask?.status === 'in_progress') stats.playlist.inProgress++;
      else stats.playlist.pending++;

      // S3 (loading_in_s3)
      const s3Task = lang['loading_in_s3'];
      if (s3Task?.status === 'done') stats.s3.done++;
      else if (s3Task?.status === 'in_progress') stats.s3.inProgress++;
      else stats.s3.pending++;
    });

    return stats;
  }, [languages]);

  // Общий прогресс для части
  const totalProgress = useMemo(() => {
    const totalDone = progress.translate.done + progress.voicing.done + progress.fullVideo.done + progress.playlist.done + progress.s3.done;
    const totalAll = (progress.translate.done + progress.translate.inProgress + progress.translate.pending) +
                     (progress.voicing.done + progress.voicing.inProgress + progress.voicing.pending) +
                     (progress.fullVideo.done + progress.fullVideo.inProgress + progress.fullVideo.pending) +
                     (progress.playlist.done + progress.playlist.inProgress + progress.playlist.pending) +
                     (progress.s3.done + progress.s3.inProgress + progress.s3.pending);
    return { done: totalDone, total: totalAll };
  }, [progress]);

  return (
    <div className="t_c_part" style={{ width: '100%', opacity: isSkipped ? 0.45 : 1, position: 'relative' }}>
      {isSkipped && (
        <Tooltip title="Эта часть пропускается при расчёте прогресса">
          <div style={{
            position: 'absolute', top: '4px', right: '8px', zIndex: 1,
            background: '#ff7875', color: '#fff', fontSize: '10px', fontWeight: 600,
            padding: '1px 6px', borderRadius: '3px', lineHeight: '16px'
          }}>
            Пропущена
          </div>
        </Tooltip>
      )}
      <div className="t_c t_c--num _fcc" style={{
        width: '6em',
        flexShrink: 0,
        alignSelf: 'center',
        alignItems: 'center',
        boxShadow: 'none',
      }}>
        {/* <div className="t_line"> */}
        <div className="t_col t_col--c">
          <div className="t_lang0" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
            <div style={{ fontSize: '2em'}}>{id}</div>
            {partDuration?.duration_formatted && (
              <Tooltip title={`Длительность части ${id}`}>
                <div style={{
                  fontSize: '12px',
                  color: '#1890ff',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                  marginTop: '4px',
                  lineHeight: 1
                }}>
                  <ClockCircleFilled style={{ fontSize: '10px' }} />
                  {partDuration.duration_formatted}
                </div>
              </Tooltip>
            )}
            {/* Прогресс этапов */}
            {languages.length > 0 && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                fontSize: '10px'
              }}>
                <ProgressIndicator {...progress.translate} label="Перевод" />
                <ProgressIndicator {...progress.voicing} label="Озвучка" />
                <ProgressIndicator {...progress.fullVideo} label="Полное видео" />
                <ProgressIndicator {...progress.playlist} label="Плейлисты" />
                <ProgressIndicator {...progress.s3} label="S3" />
              </div>
            )}
          </div>
        </div>
        {/* </div> */}
      </div>

      <div className="t_c t_c--step0" style={{ width: '16em', flexShrink: 0 }}><div><div className="t_col"><div className="t_line"><div className="t_type"><div>Распознавание</div></div><div className="t_cell"><div className="t_user"><div className="t_user_ava t_user_ava--robot"><div>🤖</div></div><div className="t_user_name">Robot</div></div><div className="t_status0"><div className="t_status t_status--done"><div>Готово</div></div><div className="t_status_date"><div>16 дек 2024</div></div></div></div></div></div></div></div>

      <div className="t_c_langs0">
        {getAllLanguages(langsShown === 'all' ? null : langsShown).map(langId => {
          let lang = languages.find(lang => lang.id === langId);
          if (!lang) lang = {
            "id": langId,
            "project_id": project_id,
            "part_id": id,
            "auto_translation": null,
            "translation_proofreading": null,
            "voicing": null,
            "voicing_proofreading": null,
            "animations_video": null,
            "animations_video_proofreading": null,
            "video": null,
            "video_proofreading": null,
            "full_video": null,
            "full_video_proofreading": null,
            "ts_playlist": null,
            "loading_in_s3": null
          };
          return <div key={langId} className="t_c_lang">
            <div className="t_c t_c--step_lang" style={{ width: '4em', flexShrink: 0 }}>
              <div className="t_line t_line--lang">
                <div className="t_lang0">
                  <div>
                    <Flag langId={langId} />
                  </div>
                </div>
              </div>
            </div>

            <div className="t_c t_c--step_translate" style={{ width: '28em', flexShrink: 0 }}>
              <div className="t_col">
                <TaskCell
                  task={lang.auto_translation}
                  taskType='auto_translation'
                  status={lang.auto_translation?.status}
                  date={lang.auto_translation?.date}
                  width='14em'
                  taskTypeName='Автоперевод'
                  taskStatus='done'
                  projectId={project_id}
                  partId={id}
                  languageId={lang.id}
                  key={`auto_translation_${lang.id}`}
                />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <TaskCell
                    task={lang.translation_proofreading}
                    status={lang.translation_proofreading?.status}
                    date={lang.translation_proofreading?.date}
                    taskType='translation_proofreading'
                    width='14em'
                    taskTypeName='Проверка перевода'
                    taskStatus='done'
                    projectId={project_id}
                    partId={id}
                    languageId={lang.id}
                  />
                  <AudioEditorLinks projectId={project_id} folderId={id} langId={lang.id} />
                </div>
              </div>
            </div>

            <div className="t_c t_c--voicing" style={{ width: '28em', flexShrink: 0 }}>
              <div className="t_col">
                <TaskCell
                  task={lang.voicing}
                  status={lang.voicing?.status}
                  date={lang.voicing?.date}
                  taskType='voicing'
                  width='14em'
                  taskTypeName='Озвучка'
                  projectId={project_id}
                  partId={id}
                  languageId={lang.id}
                />
                <TaskCell
                  task={lang.voicing_proofreading}
                  status={lang.voicing_proofreading?.status}
                  date={lang.voicing_proofreading?.date}
                  taskType='voicing_proofreading'
                  width='14em'
                  taskTypeName='Проверка озвучки'
                  taskStatus='done'
                  projectId={project_id}
                  partId={id}
                  languageId={lang.id}
                />
              </div>
            </div>

            <div className="t_c t_c--full_video" style={{ width: '14em', flexShrink: 0 }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: '1em 1em',
                gap: '4px',
              }}>
                <CompactStatusCell
                  task={lang.full_video}
                  taskType='full_video'
                  label='Видео'
                  projectId={project_id}
                  partId={id}
                  languageId={lang.id}
                />
                <CompactStatusCell
                  task={lang.full_video_proofreading}
                  taskType='full_video_proofreading'
                  label='YouTube'
                  projectId={project_id}
                  partId={id}
                  languageId={lang.id}
                />
              </div>
            </div>

            <div className="t_c t_c--ts_playlist" style={{ width: '14em', flexShrink: 0 }}>
              <div className="t_col">
                <TaskCell
                  task={lang.ts_playlist}
                  status={lang.ts_playlist?.status}
                  date={lang.ts_playlist?.date}
                  taskType='ts_playlist'
                  width='14em'
                  taskTypeName='Плейлисты'
                  projectId={project_id}
                  partId={id}
                  languageId={lang.id}
                />
              </div>
            </div>

            <div className="t_c t_c--loading_in_s3" style={{ width: '14em', flexShrink: 0 }}>
              <div className="t_col">
                <TaskCell
                  task={lang.loading_in_s3}
                  status={lang.loading_in_s3?.status}
                  date={lang.loading_in_s3?.date}
                  taskType='loading_in_s3'
                  width='14em'
                  taskTypeName='Загрузка в S3'
                  projectId={project_id}
                  partId={id}
                  languageId={lang.id}
                />
              </div>
            </div>


          </div>
        })}
      </div>
    </div>
  )
}

// Memoize component to prevent unnecessary re-renders
export const Part = React.memo(PartComponent);
